import classNames from 'classnames'
import { pageRoutes, thriveCareV1PageRoutes } from 'constants/routing'
import { Dropdown } from 'react-bootstrap'
import 'lib/reactBootstrapReact19/dropdown'
import NavUserDetails from '../NavUserDetails'
import styles from '../../index.module.scss'
import loginNavItemWrapperStyles from './index.module.scss'
import useThriveAuth from 'hooks/useThriveAuth'
import Cookies from 'js-cookie'
import { useDispatch } from 'react-redux'
import { cleanBookingInfo, clearSalesforceDetails } from 'state/booking/actions'
import { clearStoredPartnershipCode } from 'utils/b2bDetails'
import { clearNonPersistingDataInSession } from 'utils/common'
import { COOKIE_KEYS } from 'constants/common'
import {
  canAccessInternalTools,
  dashboardHeaderLinkText,
  navDropdownLinks,
} from 'constants/dashboard'
import { useContext } from 'react'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useRouter } from 'next/router'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { If } from '@/utility_components/ControlStatements'
import { canAccessNena } from '@app/care/v1/(pages)/nena/utils/helpers'
import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'
import { clearTimezone } from 'utils/timezoneStorage'
import { resolveTimezoneByTzCode } from 'utils/timezone'
import usePartnershipCodeUrl from 'hooks/usePartnershipCodeUrl'
import { withMarketingParams } from 'lib/marketingParams'

type LoginNavItemsWrapperProps = {
  loginNavItemHelpers: RenderNavItemHelpers
}

const LoginNavItemsWrapper: React.FC<LoginNavItemsWrapperProps> = ({
  loginNavItemHelpers,
  children,
}) => {
  const router = useRouter()
  const { pathname } = router
  const { resetToggle } = loginNavItemHelpers
  const { logout, isSupportOrThirdPartyUser, permissions } = useThriveAuth()
  const { setTimeZoneObject } = useContext(TimeZoneContext)
  const dispatch = useDispatch()

  const handleLogout = () => {
    resetToggle()
    Cookies.remove(COOKIE_KEYS.VISITED_FIRST_TIME_FLOW)
    clearNonPersistingDataInSession()
    clearStoredPartnershipCode()
    clearTimezone()
    dispatch(cleanBookingInfo())
    dispatch(clearSalesforceDetails())

    if (isSupportOrThirdPartyUser && pathname === pageRoutes.PROVIDER_SEARCH) {
      setTimeZoneObject(
        resolveTimezoneByTzCode(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )
      )
    }

    logout({
      returnTo: `${window.location.origin}${pageRoutes.PROVIDER_SEARCH}`,
    })
  }

  const isSalesForceB2BOn =
    useGetTreatment(TreatmentFlags.SALESFORCE_B2B_CASE_ACCOUNT) ===
    treatmentFlagValues.ON
  const { isEmbeddedInSf, hasLeadId } = useSalesforceLead()
  const showMentalHealthTopics = isSalesForceB2BOn
    ? !isEmbeddedInSf
    : !hasLeadId

  const isThrivecareNENAEnabled =
    useGetUserTreatment(TreatmentFlags.THRIVECARE_NENA) ===
    treatmentFlagValues.ON

  const addPartnershipCodeToUrl = usePartnershipCodeUrl()

  return (
    <>
      {/* Wrapper component for common functionality in Support user and 
      Normal user Navbar */}
      <NavUserDetails />
      <div className={styles.divider} />
      {children}
      <div
        className={classNames(
          loginNavItemWrapperStyles.DefaultDropdownItemContainer,
          {
            [loginNavItemWrapperStyles.DefaultDropdownItemContainer__noChildrenDropdownItems]: !children,
          }
        )}
      >
        <If condition={canAccessInternalTools(permissions)}>
          <Dropdown.Item
            className={styles.dropdown_item}
            href={pageRoutes.LOGIN_DASHBOARD}
            onClick={resetToggle}
          >
            {dashboardHeaderLinkText.INTERNAL_TOOLS}
          </Dropdown.Item>
        </If>

        {showMentalHealthTopics && (
          <Dropdown.Item
            className={styles.dropdown_item}
            href={withMarketingParams(
              addPartnershipCodeToUrl(navDropdownLinks.BLOG)
            )}
            target="_blank"
            onClick={resetToggle}
          >
            {dashboardHeaderLinkText.MENTAL_HEALTH_TOPICS}
          </Dropdown.Item>
        )}

        <If condition={canAccessNena(permissions) && isThrivecareNENAEnabled}>
          <Dropdown.Item
            className={`${styles.dropdown_item} ~whitespace-normal !~h-auto`}
            href={thriveCareV1PageRoutes.THRIVECARE_NENA}
            onClick={resetToggle}
          >
            {dashboardHeaderLinkText.THRIVECARE_NENA}
          </Dropdown.Item>
        </If>

        <Dropdown.Item
          className={classNames(
            loginNavItemWrapperStyles.dropdown_item_bottom_border_radius,
            styles.dropdown_item
          )}
          onClick={handleLogout}
        >
          Log out
        </Dropdown.Item>
      </div>
    </>
  )
}

export default LoginNavItemsWrapper
